import nlp from "compromise";
import { findPhoneNumbersInText } from "libphonenumber-js";
import _ from "lodash";

export const PII_TYPES = {
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  NATIONAL_ID: "NATIONAL_ID",
  PAYMENT_CARD: "PAYMENT_CARD",
  BANK_ACCOUNT: "BANK_ACCOUNT",
  PASSPORT: "PASSPORT",
  CLIENT_NAME_KNOWN: "CLIENT_NAME_KNOWN",
  CLIENT_NAME_HEURISTIC: "CLIENT_NAME_HEURISTIC",
  CAMPAIGN_KNOWN: "CAMPAIGN_KNOWN",
  CLIENT_COMPANY_KNOWN: "CLIENT_COMPANY_KNOWN",
  SUB_FUNCTIONAL_UNIT_KNOWN: "SUB_FUNCTIONAL_UNIT_KNOWN",
};

export const PII_CONFIDENCE = {
  HIGH: "high",
  LOW: "low",
};

// Types whose matched value may be forwarded to logPiiFlag as a `matches` entry
// (see pii-update-brief.md) - structured/known hits only. CLIENT_NAME_HEURISTIC is
// excluded (NLP false-positive risk on a value we'd be persisting).
export const MATCH_LOGGABLE_TYPES = [
  PII_TYPES.EMAIL,
  PII_TYPES.PHONE,
  PII_TYPES.NATIONAL_ID,
  PII_TYPES.PAYMENT_CARD,
  PII_TYPES.BANK_ACCOUNT,
  PII_TYPES.PASSPORT,
  PII_TYPES.CLIENT_NAME_KNOWN,
  PII_TYPES.CAMPAIGN_KNOWN,
  PII_TYPES.CLIENT_COMPANY_KNOWN,
  PII_TYPES.SUB_FUNCTIONAL_UNIT_KNOWN,
];

// \p{L}/\p{N} (any-script letter/number, see pii-gap-fix.md section 7d) replace
// the old a-zA-Z0-9 local-part range so non-Latin local parts (e.g. a Chinese
// local part before the @) are matched, without widening to punctuation that
// would start pulling in surrounding text (parens, quotes, etc.).
const EMAIL_PATTERN = /[\p{L}\p{N}._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gu;

// Markets validated against for phone detection. A `+`-prefixed number is
// unambiguous regardless of which of these is tried first; a local-format
// number (no `+`) is only recognized if it happens to be a valid number for
// one of these markets' numbering plans - see findPhones below.
const PHONE_SEARCH_COUNTRIES = ["MY", "SG", "PH", "ID", "TH", "IN", "HK", "CN"];

// Malaysian NRIC: 6 digits (YYMMDD) - 2 digit state code - 4 digits, with an
// optional single space or dash between blocks (see pii-gap-fix.md section
// 5, minor MY NRIC finding). A more permissive "separator between any two
// digits" version was tried and reverted: it made 12-digit bank-account
// numbers indistinguishable from an NRIC and suppressed the BANK_ACCOUNT hit
// for several of the formats fixed in section 5b.
const NATIONAL_ID_PATTERN = /\b\d{6}[ -]?\d{2}[ -]?\d{4}\b/g;

// Singapore NRIC/FIN: leading S/T (citizen/PR) or F/G (foreign ID) letter,
// 7 digits, trailing checksum letter, with optional single space/dash
// separators, e.g. S1234567D, S 1234567 D, S-1234567-D (see pii-gap-fix.md
// section 5a). Shares its digit-letter shape with PASSPORT_PATTERN below,
// but the restricted leading-letter set keeps false-positive overlap low in
// practice.
const SG_NRIC_PATTERN = /\b[STFGstfg][ -]?\d(?:[ -]?\d){6}[ -]?[A-Za-z]\b/g;

// Shared shapes reused by more than one gated format below - two markets can
// legitimately use the same digit grouping (see pii-gap-fix.md section 7a).
const GROUPED_12_DIGIT_PATTERN = /\b\d{4}[ -]?\d{4}[ -]?\d{4}\b/g;
// Optional single space/dash between digits - both keyword-gated uses below
// (TC Kimlik, CPF) were otherwise silently missing any separated input even
// with the gating keyword present, since the bare-\d{11} shape never matched
// in the first place for a separator to have a chance to gate on
// (the brief doc).
const PLAIN_11_DIGIT_PATTERN = /\b\d(?:[ -]?\d){10}\b/g;

// Format-specific national ID patterns for markets outside MY/SG (see
// pii-gap-fix.md section 7a). Each entry is checked independently by
// findNationalIds below. Entries with no `keywords` have a shape distinctive
// enough (fixed punctuation, a letter prefix/suffix, an unusual length) to
// stand unconditionally, same as the MY/SG patterns above. Entries with
// `keywords` are a bare digit run indistinguishable from a phone/bank/
// reference number on shape alone, so they only count as a hit when a
// market-specific keyword is nearby - same hasNearbyKeyword mechanism
// PASSPORT/BANK_ACCOUNT already use. Colombia's cédula and similar
// too-generic-to-safely-pattern-match formats are a documented, intentional
// gap rather than added here - see pii-gap-fix.md section 7a.
const NATIONAL_ID_FORMATS = [
  { pattern: NATIONAL_ID_PATTERN },
  { pattern: SG_NRIC_PATTERN },
  // Spain DNI (8 digits + check letter) / NIE (X/Y/Z + 7 digits + check letter).
  { pattern: /\b(?:\d{8}|[XYZxyz]\d{7})[A-Za-z]\b/g },
  // Hong Kong HKID: 1-2 letters, 6 digits, parenthesized check digit - the
  // parens keep this shape from colliding with PASSPORT_PATTERN. Optional
  // single space before the parens (a normal way to write it, e.g.
  // "A123456 (7)") and the check digit may also be the letter "A" (check
  // value 10), not just 0-9 (the brief doc).
  { pattern: /\b[A-Za-z]{1,2}\d{6}[ ]?\([0-9Aa]\)/g },
  // Mainland China resident ID: 17 digits plus a trailing check digit or "X".
  { pattern: /\b\d{17}[\dXx]\b/g },
  // South Korea RRN: 6-digit birth date, dash, 7-digit serial.
  { pattern: /\b\d{6}-\d{7}\b/g },
  // South Korea RRN, unpunctuated form - same bare 13 digits as Romania's
  // CNP below, so kept keyword-gated rather than loosening the dashed
  // pattern above: an unconditional bare 13-digit run risks colliding with
  // a phone number typed without a "+" or spaces (the brief doc).
  { pattern: /\b\d{13}\b/g, keywords: ["rrn", "resident registration"] },
  // Brazil CPF, punctuated form (XXX.XXX.XXX-XX or space-grouped XXX XXX
  // XXX XX - both conventions are common on real statements/forms).
  { pattern: /\b\d{3}[.\s]?\d{3}[.\s]?\d{3}[-\s]?\d{2}\b/g },
  // India Aadhaar, spaced/dashed 4-4-4 form.
  { pattern: GROUPED_12_DIGIT_PATTERN, keywords: ["aadhaar", "aadhar", "uid"] },
  // Japan "My Number" - same 4-4-4 grouped shape as Aadhaar, gated on its own
  // keyword rather than by shape.
  { pattern: GROUPED_12_DIGIT_PATTERN, keywords: ["my number", "mynumber"] },
  // Philippines PhilSys National ID - same 4-4-4 grouped shape again, gated
  // on its own keyword (the brief doc).
  {
    pattern: GROUPED_12_DIGIT_PATTERN,
    keywords: ["philsys", "psn", "philippine national id", "philippine identification"],
  },
  // Romania CNP: 13 digits, first digit 1-8 is the sex/century code, with
  // optional single space/dash separators between digits - the bare-digit
  // pattern previously never matched separated input at all, so the "cnp"
  // keyword gate never got a chance to fire (the brief doc).
  { pattern: /\b[1-8](?:[ -]?\d){12}\b/g, keywords: ["cnp"] },
  // Portugal NIF: 9 digits.
  { pattern: /\b\d{9}\b/g, keywords: ["nif"] },
  // Turkey TC Kimlik No: 11 digits.
  { pattern: PLAIN_11_DIGIT_PATTERN, keywords: ["tc kimlik", "kimlik no", "tckn"] },
  // Brazil CPF, unpunctuated form - same 11-digit shape as TC Kimlik, gated
  // on its own keyword.
  { pattern: PLAIN_11_DIGIT_PATTERN, keywords: ["cpf"] },
  // Australia TFN: 8-9 digits, optionally grouped in 3s.
  { pattern: /\b\d{3}[ -]?\d{3}[ -]?\d{2,3}\b/g, keywords: ["tfn", "tax file number"] },
  // Thailand national ID: 13 digits in a fixed 1-4-5-2-1 grouping, e.g.
  // "1 1010 12345 67 8". The leading separator is mandatory - unlike the
  // MY/China/Korea patterns above, a fully unpunctuated 13-digit run is too
  // generic to stand alone (the same genericness the keyword-gated Romania
  // CNP format above already has to guard against at 13 digits), so this
  // only matches once the grouping is actually visible (COACH-2092).
  { pattern: /\b\d[ -]\d{4}[ -]?\d{5}[ -]?\d{2}[ -]?\d\b/g },
  // India income-tax PAN: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F).
  // The 5-letter prefix exceeds PASSPORT_PATTERN's 3-letter cap, so this
  // can't collide with a passport match (COACH-2092).
  { pattern: /\b[A-Za-z]{5}\d{4}[A-Za-z]\b/g },
  // Portugal Cartão de Cidadão (citizen card) number: 8-digit base number,
  // 1 check digit, then a 2-letter + 1-digit document/version block, e.g.
  // "12345678 9 ZZ1" - distinct from the plain-9-digit NIF above because of
  // that trailing letter block (COACH-2092).
  { pattern: /\b\d{8}[ -]?\d[ -]?[A-Za-z]{2}[ -]?\d\b/g },
  // Taiwan national ID: 1 letter (area code) + 9 digits, where the 1st
  // digit is a gender code (1 or 2), e.g. A123456789. Pinning that digit
  // keeps every real ID while dropping most of the false-positive surface
  // a bare letter+9digits would have against case/order/ticket refs like
  // "S987654321" (COACH-2092).
  { pattern: /\b[A-Za-z][12]\d{8}\b/g },
];

// Candidate runs of 13-19 digits (with optional spaces/dashes as separators) for card-number checking
const PAYMENT_CARD_CANDIDATE_PATTERN = /\b(?:\d[ -]?){12,18}\d\b/g;

// Candidate runs of 8-19 digits, allowing a single space or dash between any
// two digits so grouped formats like "123-456-7890" or "1234-5678-9012" are
// still recognized as one candidate (see pii-gap-fix.md section 5b).
// Normalized digit count is what matters - separators are stripped before
// any length/dedupe check downstream. Ceiling raised from 17 to 19 to align
// with PAYMENT_CARD_CANDIDATE_PATTERN below - modern debit/UnionPay card
// numbers commonly run to 19 digits (the brief doc).
const BANK_ACCOUNT_CANDIDATE_PATTERN = /\b\d(?:[ -]?\d){7,18}\b/g;

// IBAN: 2-letter country code, 2 check digits, then 11-30 alphanumeric
// characters (total IBAN length varies 15-34 by country), optionally
// grouped with single spaces or dashes at any position - see pii-gap-fix.md
// section 7c. Gated behind the existing "iban" keyword in
// BANK_ACCOUNT_CONTEXT_KEYWORDS below, same as the bare-digit pattern above.
// The body is restricted to uppercase letters/digits (real IBANs are always
// rendered uppercase) rather than [A-Za-z0-9] - with lowercase allowed, the
// optional inter-group separator let the match run on into an ordinary
// lowercase word straight after the IBAN (e.g. "...926819 today" matching
// through "today" as if it were more grouped IBAN body). Dash added
// alongside space (the brief doc) - a dash-grouped IBAN like
// "RO49-AAAA-1B31-0075-9384-0000" was previously invisible to this pattern
// even though the space-grouped and unspaced forms of the same value both
// matched.
const IBAN_CANDIDATE_PATTERN = /\b[A-Za-z]{2}\d{2}(?:[ -]?[A-Z0-9]){11,30}\b/g;

// Brazil's labeled "Agência / Conta" bank-account format - the labels
// themselves are the context signal (see pii-gap-fix.md section 7c), so
// this pattern is unconditional rather than gated through
// BANK_ACCOUNT_CONTEXT_KEYWORDS like the two patterns above. "C/C" (conta
// corrente) is a common abbreviation for "conta" on real statements, e.g.
// "Ag. 1234 / C/C 56789-0" (COACH-2092).
const BRAZIL_LABELED_BANK_PATTERN =
  /\bag(?:[eê]ncia)?\.?:?\s*\d{2,6}\s*\/?\s*(?:conta|c\/c):?\s*\d{2,8}(?:-\d{1,2})?\b/gi;

// 0-3 letters, 6-9 digits (optionally grouped with single spaces/dashes),
// and an optional trailing checksum letter, e.g. A1234567, AB12345678,
// K1234567X (Singapore), P1234567A (Philippines), A 1234 5678 (see
// pii-gap-fix.md section 5c), ABC123456 (3-letter prefix), 123456789
// (letterless, e.g. Taiwan) (see pii-gap-fix.md section 7c). A match with at
// least one letter (leading prefix and/or trailing check letter) is
// distinctive enough not to need a nearby keyword - see findPassports below
// and COACH-2096. Only the letterless end of this range (a bare 6-9 digit
// run, indistinguishable on shape alone from an ordinary ticket/case/invoice
// reference) stays keyword-gated. The prefix/suffix letters are captured
// (groups 1/2) so findPassports can tell a real passport-shaped prefix apart
// from an ordinary short reference-label word ("Ref", "Tel", "PO", "no")
// that just happens to precede a similarly-shaped digit run.
const PASSPORT_PATTERN = /\b([A-Za-z]{0,3})[ -]?\d(?:[ -]?\d){5,8}(?:[ -]?([A-Za-z]))?\b/g;

// How many characters on either side of a candidate match count as "nearby"
// for context-keyword gating. Widened from 40 (see COACH-2096): a keyword
// mentioned once earlier in a longer sentence/paragraph was otherwise too
// easily out of range of the actual number it was labeling.
const CONTEXT_WINDOW_CHARS = 80;

// Candidate shapes for passport/bank-account are also common shapes for
// ordinary ticket/case/invoice references, so a bare regex match is only
// promoted to a hit if a relevant keyword appears within CONTEXT_WINDOW_CHARS
// of the match - see pii-gap-fix.md section 2. Widened per COACH-2096 to
// cover common real-world phrasings beyond the literal word "passport";
// deliberately multi-word/punctuated (not a bare "ic") since hasNearbyKeyword
// does a plain substring check - a short standalone keyword like "ic" would
// false-positive-match inside ordinary words (e.g. "topic", "clinic").
const PASSPORT_CONTEXT_KEYWORDS = [
  "passport",
  "travel document",
  "ic no",
  "ic number",
  "ic num",
  "nric",
  "id no",
  "id number",
  "identification no",
  "identification number",
  "passport no",
  "passport number",
  "ppt no",
];
const BANK_ACCOUNT_CONTEXT_KEYWORDS = ["bank", "account", "iban"];

// Sentence-terminating characters, used to keep hasNearbyKeyword's search
// window from bleeding into an unrelated earlier/later sentence once
// CONTEXT_WINDOW_CHARS was widened for COACH-2096 - a keyword mentioned once
// in a prior sentence could otherwise "confirm" an unrelated number several
// sentences later purely because it fell inside the wider raw character
// window. Mirrors the break characters findParticleAndGazetteerNameCandidates
// already uses (SENTENCE_TERMINATOR_PATTERN/LINE_BREAK_PATTERN below) for the
// same "don't let plain proximity cross a sentence" rationale.
const KEYWORD_SENTENCE_BREAK_PATTERN = /[.!?\n]/;

// Plain substring search (String.prototype.includes) has no notion of word
// boundaries, so a multi-word keyword can still false-match inside ordinary
// unrelated text purely by character sequence - e.g. "id no" (a
// PASSPORT_CONTEXT_KEYWORDS entry) is a literal substring of "android
// number". Matching keywords through a cached, word-boundary-guarded regex
// instead closes that gap while keeping the same "any keyword in range"
// semantics.
const keywordMatcherCache = new Map();
const getKeywordMatcher = keywords => {
  let matcher = keywordMatcherCache.get(keywords);
  if (!matcher) {
    const alternation = keywords.map(keyword => _.escapeRegExp(keyword)).join("|");
    matcher = new RegExp(`\\b(?:${alternation})\\b`, "i");
    keywordMatcherCache.set(keywords, matcher);
  }
  return matcher;
};

const hasNearbyKeyword = (text, index, matchLength, keywords) => {
  const rangeStart = Math.max(0, index - CONTEXT_WINDOW_CHARS);
  const rangeEnd = Math.min(text.length, index + matchLength + CONTEXT_WINDOW_CHARS);

  // Clamp the raw character range down to the current sentence: walk
  // backwards/forwards from the match for the nearest sentence break, rather
  // than using the full CONTEXT_WINDOW_CHARS span regardless of what it
  // crosses.
  let start = rangeStart;
  for (let i = index - 1; i >= rangeStart; i--) {
    if (KEYWORD_SENTENCE_BREAK_PATTERN.test(text[i])) {
      start = i + 1;
      break;
    }
  }
  let end = rangeEnd;
  for (let i = index + matchLength; i < rangeEnd; i++) {
    if (KEYWORD_SENTENCE_BREAK_PATTERN.test(text[i])) {
      end = i;
      break;
    }
  }

  const window = text.slice(start, end);
  return getKeywordMatcher(keywords).test(window);
};

const luhnCheck = digitsOnly => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = digitsOnly.length - 1; i >= 0; i--) {
    let digit = parseInt(digitsOnly[i], 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

const findEmails = text => (text.match(EMAIL_PATTERN) || []).map(match => ({
  type: PII_TYPES.EMAIL,
  confidence: PII_CONFIDENCE.HIGH,
  value: match,
}));

// Runs detection once per market and merges by matched text span, since the
// same local-format digit run can validate against more than one market's
// numbering plan (a `+`-prefixed number resolves the same way regardless of
// which market is tried first).
const findPhones = text => {
  const matchesByRange = new Map();

  PHONE_SEARCH_COUNTRIES.forEach(country => {
    let results = [];
    try {
      results = findPhoneNumbersInText(text, country) || [];
    } catch (error) {
      results = [];
    }

    results
      .filter(result => result.number.isValid())
      .forEach(result => {
        const key = `${result.startsAt}-${result.endsAt}`;
        if (!matchesByRange.has(key)) matchesByRange.set(key, result);
      });
  });

  return Array.from(matchesByRange.values())
    .sort((a, b) => a.startsAt - b.startsAt)
    .map(result => ({
      type: PII_TYPES.PHONE,
      confidence: PII_CONFIDENCE.HIGH,
      value: text.slice(result.startsAt, result.endsAt),
      // Internal only - see scanForPii's span-overlap dedup against
      // BANK_ACCOUNT hits below. Stripped before the hit is returned to
      // callers.
      start: result.startsAt,
      end: result.endsAt,
    }));
};

// Shared span-overlap test used to dedup/exclude hits by matched position
// rather than by digit value - see findBankAccounts and scanForPii below.
const spansOverlap = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

const findNationalIds = text =>
  NATIONAL_ID_FORMATS.flatMap(format =>
    Array.from(text.matchAll(format.pattern))
      .filter(match => !format.keywords || hasNearbyKeyword(text, match.index, match[0].length, format.keywords))
      .map(match => ({
        type: PII_TYPES.NATIONAL_ID,
        // Unconditional (distinctive-shape) formats are HIGH confidence, same
        // as the original MY/SG patterns. Keyword-gated formats are a bare
        // digit run relying entirely on nearby context, so they're LOW
        // confidence - consistent with how PASSPORT/BANK_ACCOUNT already
        // treat "generic shape + keyword gate" (see pii-gap-fix.md section 7a).
        confidence: format.keywords ? PII_CONFIDENCE.LOW : PII_CONFIDENCE.HIGH,
        value: match[0],
        // Internal only - see findBankAccounts' span-overlap exclusion
        // below (COACH-2092). Stripped before the hit is returned to callers.
        start: match.index,
        end: match.index + match[0].length,
      }))
  );

const findPaymentCards = text =>
  Array.from(text.matchAll(PAYMENT_CARD_CANDIDATE_PATTERN))
    .filter(match => {
      const digitsOnly = match[0].replace(/[ -]/g, "");
      return digitsOnly.length >= 13 && digitsOnly.length <= 19 && luhnCheck(digitsOnly);
    })
    .map(match => ({
      type: PII_TYPES.PAYMENT_CARD,
      confidence: PII_CONFIDENCE.HIGH,
      value: match[0],
      // Internal only - see findBankAccounts' span-overlap exclusion below
      // (COACH-2092). Stripped before the hit is returned to callers.
      start: match.index,
      end: match.index + match[0].length,
    }));

const findBankAccounts = text => {
  // Only Luhn-valid card numbers are excluded here, not every candidate that
  // merely shape-matches PAYMENT_CARD_CANDIDATE_PATTERN - a bank-account
  // number can coincidentally fall in the same 13-19 digit range without
  // being a real card number, and excluding on shape alone silently dropped
  // several formats fixed in pii-gap-fix.md section 5b.
  //
  // Excluded by matched *span*, not digit value (COACH-2092) - mirrors the
  // PHONE vs BANK_ACCOUNT span-overlap dedup below. A value-based Set
  // previously suppressed a legitimate, keyword-confirmed bank-account
  // mention whenever the same digits happened to also appear - anywhere else
  // in the whole text, in an unrelated sentence - in a shape that
  // unconditionally matches NATIONAL_ID or a Luhn-valid PAYMENT_CARD. This
  // intentionally leaves the original MY/SG trade-off from section 5
  // untouched: a genuine bank-account number that itself shape-matches an
  // NRIC at the *same* location is still silently excluded here rather than
  // double-counted - a known, accepted residual gap, not something this
  // change reopens.
  const cardSpans = findPaymentCards(text);
  const nationalIdSpans = findNationalIds(text);

  const numericHits = Array.from(text.matchAll(BANK_ACCOUNT_CANDIDATE_PATTERN))
    .filter(match => {
      const start = match.index;
      const end = match.index + match[0].length;
      const overlapsAny = spans => spans.some(span => spansOverlap(start, end, span.start, span.end));
      return !overlapsAny(cardSpans) && !overlapsAny(nationalIdSpans);
    })
    .filter(match => hasNearbyKeyword(text, match.index, match[0].length, BANK_ACCOUNT_CONTEXT_KEYWORDS))
    .map(match => ({ value: match[0], start: match.index, end: match.index + match[0].length }));

  const ibanHits = Array.from(text.matchAll(IBAN_CANDIDATE_PATTERN))
    .filter(match => hasNearbyKeyword(text, match.index, match[0].length, BANK_ACCOUNT_CONTEXT_KEYWORDS))
    .map(match => ({ value: match[0], start: match.index, end: match.index + match[0].length }));

  const brazilLabeledHits = Array.from(text.matchAll(BRAZIL_LABELED_BANK_PATTERN)).map(match => ({
    value: match[0],
    start: match.index,
    end: match.index + match[0].length,
  }));

  return [...numericHits, ...ibanHits, ...brazilLabeledHits].map(({ value, start, end }) => ({
    type: PII_TYPES.BANK_ACCOUNT,
    confidence: PII_CONFIDENCE.LOW,
    value,
    // Internal only - see scanForPii's span-overlap dedup against PHONE
    // hits below. Stripped before the hit is returned to callers.
    start,
    end,
  }));
};

// Short common business-reference abbreviations that can themselves fill the
// PASSPORT_PATTERN's 0-3 letter prefix slot in front of an ordinary ticket/
// case/invoice/phone digit run - e.g. "Ref 123456789", "PO 123456789", "Tel
// 123456789". (Any 4+ letter label, e.g. "Case" or "Attn", never lands in
// the captured prefix at all: it's too long for the {0,3} slot, so a phrase
// like "Case no 12345678" instead matches starting at "no" - which is
// exactly what this list is for - and "Attn 123456789" falls through to the
// ordinary letterless/keyword-gated path instead. Only list entries of 3
// letters or fewer here, since a longer one could never actually match this
// capture group.) A prefix drawn from this list isn't a distinctive
// passport/ID shape - it reads as an ordinary reference label - so it's
// treated like a letterless match and still requires a nearby keyword,
// rather than standing unconditionally.
//
// "id" and "rrn" are national-ID reference labels, not passport ones (e.g.
// Korea's Resident Registration Number is commonly written "RRN 900215
// 1234567") - without them here, a digit run that's actually a national ID
// gets misclassified/duplicated as PASSPORT purely because its own label
// happens to fill this prefix slot (COACH-2092).
const PASSPORT_PREFIX_DENYLIST = new Set(["ref", "po", "tel", "no", "ext", "fax", "qty", "id", "rrn"]);

const findPassports = text =>
  Array.from(text.matchAll(PASSPORT_PATTERN))
    // A match containing at least one letter is distinctive enough to stand
    // unconditionally (mirrors the unconditional/keyword-gated split
    // NATIONAL_ID_FORMATS already uses for the same "distinctive shape vs
    // bare digit run" trade-off - see findNationalIds and pii-gap-fix.md
    // section 7a) - UNLESS that letter is only the denylisted prefix word
    // above with no trailing checksum letter, in which case it's really just
    // an ordinary reference label wearing a passport-shaped digit run. A
    // letterless match (or a denylisted-prefix match) still needs a nearby
    // keyword, since that shape alone is indistinguishable from an ordinary
    // ticket/case/invoice reference number (COACH-2096).
    .filter(match => {
      const [, prefix, suffix] = match;
      const isDenylistedPrefixOnly = prefix && !suffix && PASSPORT_PREFIX_DENYLIST.has(prefix.toLowerCase());
      const hasDistinctiveLetter = /[A-Za-z]/.test(match[0]) && !isDenylistedPrefixOnly;
      return hasDistinctiveLetter || hasNearbyKeyword(text, match.index, match[0].length, PASSPORT_CONTEXT_KEYWORDS);
    })
    .map(match => ({ type: PII_TYPES.PASSPORT, confidence: PII_CONFIDENCE.LOW, value: match[0] }));

// Common Malaysian/Chinese/Malay/Indian name particles and honorifics that
// `compromise`'s English-tuned tagger doesn't recognize as part of a name
// (see pii-gap-fix.md section 3a). Case-insensitive.
const NAME_PARTICLES = new Set([
  "bin", "binti", "s/o", "d/o", "a/l", "a/p", "ah", "encik", "puan", "datin",
]);

// Subset of NAME_PARTICLES that grammatically only ever sit between two name
// words (never a standalone common English word) - these particles can pull
// in a non-Title-Case neighbour so an all-lowercase name built around one of
// them (e.g. "mohd ridwhan bin mahathir") is still caught. "ah"/"encik"/
// "puan"/"datin" are deliberately excluded: "ah" especially is common enough
// as an ordinary interjection/filler ("with ah, I see...") that absorbing
// its neighbours would flag ordinary sentences. See pii-gap-fix.md.
const EXPANDABLE_NAME_PARTICLES = new Set(["bin", "binti", "s/o", "d/o", "a/l", "a/p"]);

// Best-effort, non-exhaustive list of common Malaysian Chinese/Malay/Indian
// surnames and given names, compiled from well-known public naming
// conventions - not sourced from internal customer data, and not a complete
// or authoritative database. Meant to be extended over time as gaps are
// found (see pii-gap-fix.md section 3b).
const NAME_GAZETTEER = new Set([
  "tan", "lim", "wong", "lee", "ng", "ong", "goh", "yap", "teo", "koh",
  "chan", "chong", "low", "ho", "sim", "foo", "chin", "yeoh", "cheah",
  "gan", "khoo", "loh", "chua", "toh", "wee", "ang", "phua", "seah",
  "ahmad", "mohamed", "mohammad", "abdullah", "rahman", "ismail", "hassan",
  "yusof", "ibrahim", "osman", "hamid", "aziz", "razak", "hashim", "salleh",
  "kumar", "raj", "muthu", "singh", "kaur", "nair", "pillai", "reddy",
  "naidu", "krishnan", "subramaniam", "raman",
]);

// Words that commonly appear capitalized at a sentence start or as generic
// nouns, excluded from the Title-Case-run heuristic below so it doesn't
// flag ordinary sentences as candidate names.
const TITLE_CASE_STOPWORDS = new Set([
  "the", "this", "that", "these", "those", "a", "an", "it", "he", "she",
  "they", "we", "you", "please", "kindly", "thank", "thanks", "regards",
  "hi", "hello", "dear", "reach", "call", "office",
  // Sentence-connector/transition words that are routinely capitalized only
  // because they open a sentence (often right after a line break in
  // multi-line notes) - without excluding them, one of these immediately
  // before a real name (e.g. "Regarding Jane Doe, ...") gets swept into the
  // same Title-Case run and reported as "Regarding Jane Doe" instead of
  // "Jane Doe" (COACH-2093).
  "regarding", "later", "meanwhile", "however", "furthermore", "additionally",
  "moreover", "therefore", "consequently", "accordingly", "nonetheless",
  "nevertheless", "subsequently", "afterward", "afterwards", "then", "since",
  "because", "during", "following", "currently", "recently", "previously",
  "finally", "overall", "also", "still", "so", "now",
]);

// Known vendor/platform/organization names (lowercased, full phrase) that
// would otherwise match the multi-word Title-Case-run branch below as a
// candidate person name. Best-effort, non-exhaustive - extend as real
// false positives turn up (see pii-gap-fix.md section 5d).
const ORGANIZATION_NAME_STOPWORDS = new Set(["alibaba cloud", "disney cruise"]);

// Matches a run of letters, allowing a single internal "/" or "-" so
// particles like "s/o", "d/o", "a/l", "a/p" and hyphenated given/family
// names (e.g. "Kian-wee", "Ah-Boy") are captured as one token rather than
// fragmenting at the separator - without the "-" join, the second half of a
// hyphenated name was a separate, all-lowercase token that never qualified
// as a name candidate on its own, silently truncating the run (e.g. "Elly
// Kian-wee" was reported as just "Elly Kian" - the brief doc). \p{L}
// (any-script letter, see pii-gap-fix.md section 7f) instead of A-Za-z so a
// diacritic name (Turkish, Vietnamese, Portuguese, Romanian, ...) tokenizes
// as one word instead of fragmenting at the accented character.
const NAME_TOKEN_PATTERN = /\p{L}+(?:[/-]\p{L}+)*/gu;

// \p{Lu}/\p{Ll} (any-script upper/lowercase letter, see pii-gap-fix.md
// section 7f) instead of A-Z/a-z so a capitalized diacritic name still
// qualifies, matching the widened NAME_TOKEN_PATTERN above. A hyphenated
// token (see NAME_TOKEN_PATTERN above) is checked segment-by-segment: the
// first segment must still be full Title Case, but a segment after a hyphen
// may also be all-lowercase (e.g. "wee" in "Kian-wee") - both capitalization
// conventions are common for this shape of name (the brief doc).
const isTitleCaseWord = token =>
  token.split("-").every((segment, index) => (index === 0 ? /^\p{Lu}\p{Ll}+$/u : /^\p{Lu}?\p{Ll}+$/u).test(segment));

// Lightweight companion to compromise's tagger: flags a maximal run of
// consecutive Title-Case words and/or NAME_PARTICLES tokens (length >= 2),
// or a single Title-Case token that's in NAME_GAZETTEER, as a candidate
// name - independent of whether compromise's English-tuned tagger
// recognized it. Only whitespace/punctuation may separate run tokens - except
// a sentence-ending '.', '!' or '?' (SENTENCE_TERMINATOR_PATTERN, COACH-2092)
// or a line break (LINE_BREAK_PATTERN, COACH-2093), either of which always
// breaks the run; any other intervening word also breaks it. This can still
// over- or under-match on unusual sentence structure - it's a
// recall-boosting heuristic, not a precise parser.
const isCoreNameCandidateToken = token => {
  const normalized = token.toLowerCase();
  return (isTitleCaseWord(token) && !TITLE_CASE_STOPWORDS.has(normalized)) || NAME_PARTICLES.has(normalized);
};

// A word that isn't itself a candidate can still be pulled into the run if
// it sits immediately beside an EXPANDABLE_NAME_PARTICLES token - bounded to
// one hop so it doesn't chain across an entire lowercase sentence (e.g. it
// must not sweep in "with" from "...spoke with Tan Ah Kow...").
const isAbsorbedByAdjacentParticle = (tokens, index) => {
  if (TITLE_CASE_STOPWORDS.has(tokens[index].toLowerCase())) return false;
  const leftToken = index > 0 ? tokens[index - 1].toLowerCase() : null;
  const rightToken = index < tokens.length - 1 ? tokens[index + 1].toLowerCase() : null;
  return EXPANDABLE_NAME_PARTICLES.has(leftToken) || EXPANDABLE_NAME_PARTICLES.has(rightToken);
};

// A '.', '!' or '?' between two tokens always ends a name run - unlike a
// comma, which intentionally keeps a run open to catch comma-separated name
// order (see isCoreNameCandidateToken above). Without this, a sentence
// boundary such as "...spoke with John Doe. Shared the update..." merges the
// next sentence's capitalized word ("Shared") into the name (COACH-2092).
const SENTENCE_TERMINATOR_PATTERN = /[.!?]/;

// A line break between two tokens also always ends a name run, same as a
// sentence terminator above. Multi-line coaching notes are routinely
// structured as one value per line (or one value per paragraph, separated by
// a blank line) with no sentence-ending punctuation at all between entries -
// without this, two entirely separate Title-Case phrases on adjacent lines
// (e.g. two different client company names, or two different people's names,
// each on its own line) merge into a single run purely because nothing but
// whitespace sits between them (COACH-2093).
const LINE_BREAK_PATTERN = /\n/;

const findParticleAndGazetteerNameCandidates = text => {
  const matches = Array.from(text.matchAll(NAME_TOKEN_PATTERN));
  const tokens = matches.map(match => match[0]);
  const candidateFlags = tokens.map(
    (token, index) => isCoreNameCandidateToken(token) || isAbsorbedByAdjacentParticle(tokens, index)
  );
  const candidates = [];
  let currentRun = [];

  const closeRun = () => {
    if (currentRun.length >= 2) {
      const candidate = currentRun.join(" ");
      if (!ORGANIZATION_NAME_STOPWORDS.has(candidate.toLowerCase())) {
        candidates.push(candidate);
      }
    } else if (currentRun.length === 1 && NAME_GAZETTEER.has(currentRun[0].toLowerCase())) {
      candidates.push(currentRun[0]);
    }
    currentRun = [];
  };

  tokens.forEach((token, index) => {
    if (index > 0) {
      const previousMatch = matches[index - 1];
      const gapText = text.slice(previousMatch.index + previousMatch[0].length, matches[index].index);
      if (SENTENCE_TERMINATOR_PATTERN.test(gapText) || LINE_BREAK_PATTERN.test(gapText)) {
        closeRun();
      }
    }

    if (candidateFlags[index]) {
      currentRun.push(token);
    } else {
      closeRun();
    }
  });
  closeRun();

  return candidates;
};

// Additional, independent recall path: compromise's #FirstName/#LastName
// lexicon tags are looked up by known-word membership, not by the input's
// capitalization, so this catches common Western names typed in lowercase
// (e.g. "john smith") without touching the Title-Case-run heuristic above.
// Verified empirically to have no false-positive overlap with ordinary
// sentences or the particle/gazetteer path's own false-positive risks - see
// pii-gap-fix.md.
const findLexiconNameCandidates = text => nlp(text).match("#FirstName+ #LastName+").out("array");

const normalizeName = value => (value || "").trim().toLowerCase();

// Drops any candidate whose normalized value is a proper substring of
// another candidate's (keeping the more complete match, e.g. prefers
// "Ah Wong" over a redundant "Wong"), or whose words are a proper subset of
// another candidate's words regardless of order/contiguity (e.g. prefers
// "Google ETO SRD NA" over a redundant "Google ETO NA" - COACH-2113), then
// dedupes exact repeats.
//
// Substring alone doesn't catch the word-subset case because a hyphenated
// token like "FIN-SGP" has no space for "SGP" to be a separate word of, so
// it never appears in wordsOf("FIN-SGP") - it's still a proper *substring*
// of the string "FIN-SGP" though, which is exactly what the substring check
// below is for. The two checks catch different shapes of redundancy, so
// both are needed.
const wordsOf = normalized => normalized.split(/\s+/).filter(Boolean);

// A plain, boundary-blind String.includes was previously used here, which
// meant a strictly shorter name could be wrongly dropped just because its
// characters happened to appear inside a longer one - e.g. "zhang li" reads
// as a raw character-substring of "zhang liu" even though they're two
// different names with different second words, not a "more complete"
// version of the same one (the brief doc). This checks that the match is
// bounded by a non-letter character (or the start/end of the string) on
// both sides, so a name can't be "contained in" another just by sharing a
// character run mid-word. That still lets "sgp" count as embedded in the
// hyphenated token "fin-sgp" (bounded by "-" and the end of string), which
// is exactly the case the substring check exists for per the comment above.
const isEmbeddedWithLetterBoundary = (needle, haystack) => {
  let fromIndex = 0;
  while (true) {
    const start = haystack.indexOf(needle, fromIndex);
    if (start === -1) return false;
    const beforeChar = haystack[start - 1];
    const afterChar = haystack[start + needle.length];
    const leftIsBoundary = !beforeChar || !/\p{L}/u.test(beforeChar);
    const rightIsBoundary = !afterChar || !/\p{L}/u.test(afterChar);
    if (leftIsBoundary && rightIsBoundary) return true;
    fromIndex = start + 1;
  }
};

const dedupeNameCandidates = candidates => {
  const withNormalized = candidates
    .map(value => ({ value, normalized: normalizeName(value) }))
    .filter(({ normalized }) => normalized)
    .map(entry => ({ ...entry, words: wordsOf(entry.normalized) }));

  const isProperSubstringOfAnother = (normalized, index) =>
    withNormalized.some(
      (other, otherIndex) =>
        otherIndex !== index && normalized !== other.normalized && isEmbeddedWithLetterBoundary(normalized, other.normalized)
    );

  const isProperWordSubsetOfAnother = (words, index) =>
    withNormalized.some((other, otherIndex) => {
      if (otherIndex === index || words.length >= other.words.length) return false;
      const otherWordSet = new Set(other.words);
      return words.every(word => otherWordSet.has(word));
    });

  const seen = new Set();
  const result = [];
  withNormalized.forEach(({ value, normalized, words }, index) => {
    if (isProperSubstringOfAnother(normalized, index) || isProperWordSubsetOfAnother(words, index) || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(value);
  });
  return result;
};

// Role/title words that legitimately appear inside a coach or coachee display
// name (e.g. "FR CHN Coach") but are also common in ordinary coaching-session
// text. Excluded from single-word matching so a known name is only flagged
// when it's genuinely present, not just because the text contains the word
// "coach" (COACH-2092). Scoped to name matching only - campaign codes and
// client company names don't share this false-positive class.
const KNOWN_NAME_PART_STOPWORDS = new Set([
  "coach",
  "coachee",
  "trainer",
  "trainee",
  "supervisor",
  "manager",
  "lead",
  "agent",
  "team",
]);

// Unicode-aware equivalent of `\b...\b`. JS's `\b` is defined in terms of the
// ASCII-only `\w` class, so it never asserts true around a value built
// entirely from non-ASCII letters (e.g. "Şükrü Beşiktaşlıoğlu") - both the
// value and the whitespace/punctuation around it are equally "non-word" to
// `\b`, so the regex silently never matches at all, not even a verbatim
// mention. Negative lookaround on \p{L}/\p{N} (same any-script rationale
// EMAIL_PATTERN already uses above) gives an equivalent boundary that still
// works for a known name/company written in any script.
const wordBoundaryRegex = value => new RegExp(`(?<![\\p{L}\\p{N}])${_.escapeRegExp(value)}(?![\\p{L}\\p{N}])`, "u");

// Memoized per-value word-boundary regexes for matchesKnownName below -
// without this, matchesFullName/matchesPart/partOccurrenceIndices each
// compile a fresh RegExp per name-part, per known value, on every
// scanForPii call (e.g. per keystroke via usePiiGuard.checkField). Cost
// scales with the campaign/company/name list size rather than the input
// length, so a few hundred entries means hundreds of recompiled regexes per
// call. Values come from a cached fetch, so they're stable and safe to
// cache across calls - same trade-off as keywordMatcherCache above.
const wordBoundaryTestCache = new Map();
const getWordBoundaryTestRegex = value => {
  let regex = wordBoundaryTestCache.get(value);
  if (!regex) {
    regex = wordBoundaryRegex(value);
    wordBoundaryTestCache.set(value, regex);
  }
  return regex;
};
// Separate cache for the "gu"-flagged variant matchAll needs - matchAll
// clones the regex internally rather than mutating the passed-in object's
// lastIndex, so reusing one cached instance across calls is safe.
const wordBoundaryMatchAllCache = new Map();
const getWordBoundaryMatchAllRegex = value => {
  let regex = wordBoundaryMatchAllCache.get(value);
  if (!regex) {
    regex = new RegExp(wordBoundaryRegex(value).source, "gu");
    wordBoundaryMatchAllCache.set(value, regex);
  }
  return regex;
};

// How many characters apart matchesNamePart's separate word-occurrences are
// allowed to be while still counting as "the same mention". Wide enough to
// tolerate a value split across a line break (COACH-2093's stated goal), but
// bounded so two words that only ever appear on opposite sides of a long,
// unrelated document (e.g. "Green tea" in one paragraph, "Death Valley"
// several paragraphs later) don't get treated as a "Green Valley" mention.
const NAME_PART_PROXIMITY_CHARS = 200;

const matchesKnownName = (normalizedText, normalizedName, type) => {
  if (!normalizedName) return false;
  const nameParts = normalizedName
    .split(/\s+/)
    .filter(part => part.length > 1)
    .filter(part => type !== PII_TYPES.CLIENT_NAME_KNOWN || !KNOWN_NAME_PART_STOPWORDS.has(part));
  if (nameParts.length === 0) return false;
  // Word-boundary guarded, same as matchesPart below - a plain `includes()` would let a short
  // known value match as a bare letter-substring of an unrelated longer word (e.g. campaign
  // code "GIT" matching inside "digit"), producing a false positive with no real mention of
  // the known value anywhere in the text (COACH-2104).
  const matchesFullName = getWordBoundaryTestRegex(normalizedName).test(normalizedText);
  const matchesPart = part => getWordBoundaryTestRegex(part).test(normalizedText);
  const partOccurrenceIndices = part =>
    Array.from(normalizedText.matchAll(getWordBoundaryMatchAllRegex(part)), m => m.index);
  // Every part appears *somewhere*, but not necessarily adjacent - true for every
  // occurrence pinned within NAME_PART_PROXIMITY_CHARS of some shared anchor point,
  // rather than merely present anywhere in the whole (possibly very long) text.
  const partsOccurNearby = parts => {
    const [firstPart, ...restParts] = parts;
    const restIndices = restParts.map(partOccurrenceIndices);
    if (restIndices.some(indices => indices.length === 0)) return false;
    return partOccurrenceIndices(firstPart).some(anchor =>
      restIndices.every(indices => indices.some(i => Math.abs(i - anchor) <= NAME_PART_PROXIMITY_CHARS))
    );
  };

  // CLIENT_NAME_KNOWN (coach/coachee) intentionally matches on any single
  // part - people are routinely referred to by first name alone ("Sarah
  // called"), and there's normally just the one coach/coachee pair to check
  // against, so this isn't at risk of cross-contaminating a different known
  // value.
  //
  // CAMPAIGN_KNOWN/CLIENT_COMPANY_KNOWN are checked against a whole list of
  // campaigns/companies, so the same any-single-part match let two distinct
  // entries that merely share one word (e.g. "Acme Logistics" vs "Acme
  // Holdings") cross-contaminate: mentioning one flagged the other as
  // present too, even though its other word(s) never appeared anywhere in
  // the text. Require every word to appear for these types instead - still
  // tolerant of a name split across a line break, since this doesn't
  // require the words to be strictly adjacent, but bounded to
  // NAME_PART_PROXIMITY_CHARS of each other so two words that are only ever
  // far apart in an unrelated part of the text don't count as one mention
  // (COACH-2093).
  const matchesNamePart =
    type === PII_TYPES.CLIENT_NAME_KNOWN
      ? nameParts.some(matchesPart)
      : partsOccurNearby(nameParts);

  return matchesFullName || matchesNamePart;
};

const findKnownValues = (text, values, type) => {
  const normalizedText = text.toLowerCase();

  const matchedValues = values
    .filter(Boolean)
    .filter(value => matchesKnownName(normalizedText, normalizeName(value), type));

  // A known value that's a proper substring of another matched known value
  // (e.g. functional unit "SGP" inside sub-functional-unit "FIN-SGP") reads
  // as its own word-boundary match but isn't a separate occurrence in the
  // text - dedupe to the more complete value, same as dedupeNameCandidates
  // already does for heuristic name candidates (COACH-2095).
  return dedupeNameCandidates(matchedValues).map(value => ({
    type,
    confidence: PII_CONFIDENCE.HIGH,
    value: value.trim(),
  }));
};

const findKnownNames = (text, names) => findKnownValues(text, names, PII_TYPES.CLIENT_NAME_KNOWN);

const findKnownCampaigns = (text, campaigns) => findKnownValues(text, campaigns, PII_TYPES.CAMPAIGN_KNOWN);

const findKnownClientCompanies = (text, clientCompanyNames) =>
  findKnownValues(text, clientCompanyNames, PII_TYPES.CLIENT_COMPANY_KNOWN);

// sub_functional_unit_name (e.g. "Payments Support") is a decrypted display
// name distinct from the plaintext functional_unit/sub_functional_unit
// *codes* already covered by CAMPAIGN_KNOWN, the same way CLIENT_COMPANY_KNOWN's
// campaign_name is distinct from those codes (COACH-2113). Kept as its own
// PII type rather than folded into CAMPAIGN_KNOWN or CLIENT_COMPANY_KNOWN so
// the PII panel can still say precisely which kind of known value was found.
const findKnownSubFunctionalUnits = (text, subFunctionalUnitNames) =>
  findKnownValues(text, subFunctionalUnitNames, PII_TYPES.SUB_FUNCTIONAL_UNIT_KNOWN);

// compromise's people().out("array") sometimes includes punctuation stuck to
// the first/last word of a match (e.g. a trailing "." or "," from the
// sentence itself) - trim it so downstream dedupe/display isn't showing
// "grace period." or "tom," as the flagged value. See pii-gap-fix.md section 6.
const stripEdgePunctuation = value => (value || "").replace(/^[^A-Za-z]+|[^A-Za-z]+$/g, "");

const findHeuristicNames = (text, names) => {
  const normalizedNames = new Set(names.map(normalizeName).filter(Boolean));
  const detectedPeople = (nlp(text).people().out("array") || []).map(stripEdgePunctuation);
  const particleAndGazetteerNames = findParticleAndGazetteerNameCandidates(text);
  const lexiconNames = (findLexiconNameCandidates(text) || []).map(stripEdgePunctuation);

  return dedupeNameCandidates([...detectedPeople, ...particleAndGazetteerNames, ...lexiconNames])
    .filter(name => !normalizedNames.has(normalizeName(name)))
    .map(name => ({ type: PII_TYPES.CLIENT_NAME_HEURISTIC, confidence: PII_CONFIDENCE.LOW, value: name }));
};

/**
 * Scans free text for PII/sensitive data. Each hit includes the matched
 * substring (`value`) for on-screen display. Callers may also forward `value`
 * to logPiiFlag as a `matches` entry, but only for types in
 * MATCH_LOGGABLE_TYPES - see pii-update-brief.md for the logging contract.
 */
export const scanForPii = (
  text,
  {
    coacheeName,
    coachName,
    campaigns = [],
    clientCompanyNames = [],
    subFunctionalUnitNames = [],
  } = {}
) => {
  if (!text || !text.trim()) return [];

  const knownNames = [coacheeName, coachName];
  // Also exclude known campaign/client-company/sub-functional-unit values
  // from the generic heuristic pass - otherwise a value already recognized
  // as CAMPAIGN_KNOWN/CLIENT_COMPANY_KNOWN/SUB_FUNCTIONAL_UNIT_KNOWN below
  // gets double-flagged (and, since findKnownCampaigns/findKnownClientCompanies/
  // findKnownSubFunctionalUnits run after this, mislabeled) as a generic
  // CLIENT_NAME_HEURISTIC "Name" hit too. See COACH-2094, COACH-2113.
  const heuristicExclusions = [
    ...knownNames,
    ...campaigns,
    ...clientCompanyNames,
    ...subFunctionalUnitNames,
  ];

  // A bare digit run can look like a phone number by shape alone (findPhones has no keyword
  // gate, unlike every other finder here) while also being a keyword-confirmed bank account
  // number (e.g. "Bank Account: 12-3456-7890-12") - without this, the same span gets flagged as
  // both PHONE and BANK_ACCOUNT. Suppression is by matched *span* (start/end), not by digit
  // value: comparing values alone would also suppress a genuinely distinct phone number
  // elsewhere in the text that just happens to share the same digits as a bank account number
  // mentioned somewhere else (e.g. the same number quoted once as a phone and, unrelatedly,
  // once as a bank account) - a real occurrence at its own location shouldn't disappear because
  // of a coincidental digit match somewhere else. See COACH-2105.
  const bankAccountHits = findBankAccounts(text);

  return [
    ...findEmails(text),
    ...findPhones(text)
      .filter(
        phoneHit => !bankAccountHits.some(bankHit => spansOverlap(phoneHit.start, phoneHit.end, bankHit.start, bankHit.end))
      )
      .map(({ start, end, ...hit }) => hit),
    ...findNationalIds(text).map(({ start, end, ...hit }) => hit),
    ...findPaymentCards(text).map(({ start, end, ...hit }) => hit),
    ...bankAccountHits.map(({ start, end, ...hit }) => hit),
    ...findPassports(text),
    ...findKnownNames(text, knownNames),
    ...findHeuristicNames(text, heuristicExclusions),
    ...findKnownCampaigns(text, campaigns),
    ...findKnownClientCompanies(text, clientCompanyNames),
    ...findKnownSubFunctionalUnits(text, subFunctionalUnitNames),
  ];
};

export const hasPii = (text, options) => scanForPii(text, options).length > 0;
